import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { ArchiveCardModal } from "../../components/ArchiveCardModal";
import { CardRow } from "../../components/CardRow";
import { InventoryTabs } from "../../components/InventoryTabs";
import { LoadMoreSentinel } from "../../components/LoadMoreSentinel";
import { LoadFailed, Loading } from "../../components/LoadState";
import { SetPriceModal } from "../../components/SetPriceModal";
import { StockLevelModal } from "../../components/StockLevelModal";
import { formatCents } from "../../core/primitives/card-display";
import { displayedAvailable } from "../../core/primitives/stock-list";
import { PriceHistoryModal } from "../../sections/price-history/price-history-modal";
import { PriceHistoryActions } from "../../sections/price-history/price-history-model";
import { CARD_FILTERS, CardsActions, CardsState } from "./cards-model";

export function CardsPage() {
  const state = useSubtree(CardsState);
  const actions = useSubtree(CardsActions);
  const history = useSubtree(PriceHistoryActions);
  const observer = useObserver();

  const filter = observer.watch(state.filter);
  const search = observer.watch(state.search);
  const status = observer.watch(state.status);
  const items = observer.watch(state.items);
  const nextCursor = observer.watch(state.nextCursor);
  const busyCardId = observer.watch(state.busyCardId);
  const notice = observer.watch(state.notice);
  const dialog = observer.watch(state.dialog);
  const dialogError = observer.watch(state.dialogError);
  const canEditInventory = observer.watch(state.canEditInventory);
  const stockEdits = observer.watch(state.stockEdits);
  const stockValue = observer.watch(state.stockValueCents);
  const reviewCount = observer.watch(state.reviewCount);

  const loading = status.kind === "idle" || status.kind === "loading";
  const error = status.kind === "error";

  return (
    <div className="mx-auto max-w-7xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div className="panel p-4 sm:p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-lg font-bold text-white">Inventory</h1>
          <InventoryTabs active="stock" reviewCount={reviewCount} />
          <div className="hidden gap-2 sm:flex">
            <Link to="/drafts?bulk=1" className="btn-secondary btn-sm">
              Bulk Scan
            </Link>
            <Link to="/scan" className="btn-primary btn-sm">
              New Scan
            </Link>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {CARD_FILTERS.map((tab) => (
              <button
                type="button"
                key={tab.key}
                onClick={() => actions.setFilter(tab.key)}
                className={`rounded-btn px-3 py-1.5 text-xs font-medium transition-colors ${
                  filter === tab.key
                    ? "bg-accent text-ink"
                    : "bg-surface text-muted hover:bg-surface-strong hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            placeholder="Search player, brand, set…"
            className="field max-w-xs"
            value={search}
            onChange={(e) => actions.setSearch(e.target.value)}
          />
        </div>

        {items.length > 0 && (
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
            {items.length}
            {nextCursor ? "+" : ""} {items.length === 1 ? "card" : "cards"}
            {stockValue !== null && stockValue > 0 && (
              <span className="text-faint"> · {formatCents(stockValue)} in stock</span>
            )}
          </p>
        )}

        {notice && (
          <div className="note note-accent mb-4 flex items-center justify-between gap-3 text-accent">
            <span className="truncate">{notice}</span>
            <button type="button" onClick={actions.dismissNotice} className="btn-ghost shrink-0">
              Dismiss
            </button>
          </div>
        )}

        {status.kind === "error" && (
          <LoadFailed title="Couldn't load your cards." message={status.message} onRetry={actions.retry} />
        )}

        {loading && items.length === 0 ? (
          <Loading label="Loading your cards…" />
        ) : error && items.length === 0 ? null : items.length === 0 ? (
          <div className="py-10 text-center">
            <p className="mb-4 text-sm text-muted">
              {search || filter !== "all" ? "No cards match that." : "No cards yet."}
            </p>
            {!search && filter === "all" && (
              <Link to="/scan" className="btn-primary btn-sm">
                Scan a card
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <CardRow
                key={item.card.id}
                item={item}
                busy={busyCardId === item.card.id}
                canEdit={canEditInventory}
                onSetPrice={() => actions.openSetPrice(item.card)}
                onRefreshPrice={() => void actions.reprice(item.card)}
                onArchive={() => actions.openArchive(item.card)}
                onSetStock={() => actions.openStock(item.card)}
                onHistory={() => void history.open(item.card)}
                available={displayedAvailable(item.card, stockEdits)}
                onStep={(step) => actions.stepStock(item.card, step)}
              />
            ))}
            {nextCursor && status.kind !== "error" && (
              <LoadMoreSentinel onVisible={() => void actions.loadMore()} loading={loading} />
            )}
          </div>
        )}
      </div>

      {dialog?.kind === "price" && (
        <SetPriceModal
          card={dialog.card}
          busy={busyCardId === dialog.card.id}
          error={dialogError}
          onSave={(next) => void actions.savePrices(next)}
          onCancel={actions.closeDialog}
        />
      )}

      {dialog?.kind === "archive" && (
        <ArchiveCardModal
          card={dialog.card}
          busy={busyCardId === dialog.card.id}
          error={dialogError}
          onConfirm={() => void actions.confirmArchive()}
          onCancel={actions.closeDialog}
        />
      )}

      {dialog?.kind === "stock" && (
        <StockLevelModal
          card={dialog.card}
          busy={busyCardId === dialog.card.id}
          error={dialogError}
          onSave={(change) => void actions.saveStock(change)}
          onCancel={actions.closeDialog}
        />
      )}

      <PriceHistoryModal />

      {canEditInventory && (
        <Link to="/scan" className="btn-primary fixed right-4 bottom-6 z-20 rounded-full px-5 shadow-lg sm:hidden">
          Scan cards
        </Link>
      )}
    </div>
  );
}
